import type { EnumOption, EnumRenderHint, EnumSetterWidget } from '../../../widgets';
import {
  BindingRow,
  ColourField,
  ListEditor,
  OptionalBindingRow,
  Row,
  ScalarField,
  Section,
  SelectField,
  TextField,
  formatScalar,
  patcher,
  type WidgetEditorProps,
} from '../fields';

const HINTS: { value: EnumRenderHint; label: string }[] = [
  { value: 'auto', label: 'Automatisch (Segmente bis 4, Kacheln bis 8, sonst Liste)' },
  { value: 'segmented', label: 'Segmente' },
  { value: 'tile', label: 'Kacheln' },
  { value: 'dropdown', label: 'Liste' },
];

/** Auswahl-Steller. Die Optionsliste ist der eigentliche Inhalt: Wert,
 *  Beschriftung, Symbol und Farbe je Eintrag, in der Reihenfolge, in der sie
 *  auf dem Schirm stehen. */
export function EnumSetterEditor(
  { widget, plantId, missing, onChange }: WidgetEditorProps<EnumSetterWidget>,
) {
  const patch = patcher(widget, onChange);
  return (
    <>
      <Section title="Bindung">
        <BindingRow
          label="Befehls-Datenpunkt"
          value={widget.commandPoint}
          plantId={plantId}
          filter={{ writable: true }}
          onChange={(next) => patch({ commandPoint: next })}
        />
        <OptionalBindingRow
          label="Rückmelde-Datenpunkt"
          value={widget.feedbackPoint}
          plantId={plantId}
          hint="Ohne Rückmeldung zeigt das Widget nur den gesendeten, nie den bestätigten Zustand."
          onChange={(next) => patch({ feedbackPoint: next })}
        />
      </Section>
      <Section title="Darstellung">
        <SelectField
          label="Form"
          value={widget.renderHint ?? 'auto'}
          options={HINTS}
          onChange={(next) => patch({ renderHint: next })}
        />
      </Section>
      <Section title="Optionen">
        <ListEditor<EnumOption>
          label="Optionen"
          required
          missing={missing.has('options')}
          items={widget.options}
          addLabel="Option"
          emptyHint="Ohne Optionen hat der Steller nichts zu stellen."
          create={() => ({ value: undefined, label: '' })}
          itemTitle={(o, i) => o.label || formatScalar(o.value) || `Option ${i + 1}`}
          onChange={(next) => patch({ options: next })}
          renderItem={(option, patchOption) => (
            <>
              <Row>
                <ScalarField
                  label="Wert"
                  value={option.value}
                  required
                  onChange={(next) => patchOption({ value: next })}
                />
                <TextField
                  label="Beschriftung"
                  value={option.label}
                  keepEmptyString
                  required
                  onChange={(next) => patchOption({ label: next ?? '' })}
                />
              </Row>
              <Row>
                <TextField
                  label="Symbol"
                  value={option.iconRef}
                  placeholder="z. B. flame"
                  onChange={(next) => patchOption({ iconRef: next })}
                />
                <ColourField
                  label="Farbe"
                  value={option.colour}
                  onChange={(next) => patchOption({ colour: next })}
                />
              </Row>
            </>
          )}
        />
      </Section>
    </>
  );
}
